import { useMemo } from 'react';
import { cn } from '@/shared/lib/cn';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import { LlevaFacturaTag } from './LlevaFacturaTag';
import { CajaNombre } from './CajaNombre';

/**
 * Caja picker for Tesorería. Lists every active `Medio_MDP` grouped by
 * `Categoria_MDP` (Local vs Internacional). Selected value is the
 * `Medio_MDP` name string — matches `MovimientoCajas.MedioPago_MC`
 * exactly so the backend filter doesn't need any translation.
 */
export function CajaSelect({
  value,
  onChange,
  medios,
  loading,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  medios: MedioDePagoItem[];
  loading: boolean;
  /** Ancho del trigger: por defecto el de la barra de Tesorería (220px en desktop). */
  className?: string;
}) {
  const grouped = useMemo(() => {
    const active = medios.filter((m) => m.fields.Status_MDP === 'Activo');
    const local = active.filter((m) => m.fields.Categoria_MDP === 'Local');
    const intl = active.filter((m) => m.fields.Categoria_MDP === 'Internacional');
    return { local, intl };
  }, [medios]);

  const item = (m: MedioDePagoItem) => (
    <SelectItem
      key={m.id}
      value={m.fields.Medio_MDP ?? ''}
      // En la lista, no en el trigger: ahí la etiqueta cortaba el nombre.
      trailing={<LlevaFacturaTag show={m.fields.LlevaFactura_MDP === 'SI'} />}
    >
      <CajaNombre caja={m.fields.Medio_MDP} tipo={m.fields.Tipo_MDP} />
    </SelectItem>
  );

  return (
    <Select value={value} onValueChange={onChange} disabled={loading}>
      <SelectTrigger className={cn('w-full sm:w-[220px]', className)}>
        <SelectValue placeholder={loading ? 'Cargando…' : 'Seleccionar caja…'} />
      </SelectTrigger>
      <SelectContent>
        {grouped.local.length > 0 && (
          <SelectGroup>
            <SelectLabel>Pesos</SelectLabel>
            {grouped.local.map(item)}
          </SelectGroup>
        )}
        {grouped.intl.length > 0 && (
          <SelectGroup>
            <SelectLabel>Dólares</SelectLabel>
            {grouped.intl.map(item)}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}
